import { useEffect, useRef, type ReactNode } from 'react';

import type { Item } from '../api/types.js';

/**
 * The timeline list (M1.16.1).
 *
 * ## Infinite scroll, driven by the cursor
 *
 * An `IntersectionObserver` on a sentinel at the end of the list, rather than
 * a scroll handler doing arithmetic: it fires once when the end comes into
 * view, costs nothing while it does not, and does not need to know how tall
 * anything is.
 *
 * The sentinel stays rendered while `hasMore` is true even if the page came
 * back empty — under `filter=unread` a page legitimately contains nothing
 * while the list goes on, so "no items arrived" is not the end.
 *
 * ## Focus follows selection
 *
 * The selected row is scrolled into view and focused, which is M1.17.1's
 * second criterion and also what makes the keyboard work for a screen reader:
 * moving selection without moving focus leaves the reader announcing the row
 * somebody left five items ago.
 */

export interface TimelineListProps {
  readonly items: readonly Item[];
  readonly selectedId: string | undefined;
  readonly loading: boolean;
  readonly hasMore: boolean;
  readonly showCounts: boolean;
  readonly onSelect: (item: Item) => void;
  readonly onToggleRead: (item: Item) => void;
  /** Stars or unstars a row (M4.2.3). */
  readonly onToggleStar?: ((item: Item) => void) | undefined;
  readonly onEndReached: () => void;
  readonly onScrolled: () => void;
}

export function TimelineList(props: TimelineListProps): ReactNode {
  const sentinel = useRef<HTMLLIElement | null>(null);
  const { onEndReached, hasMore } = props;

  useEffect(() => {
    const element = sentinel.current;
    if (element === null || !hasMore) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onEndReached();
        }
      },
      // A screen ahead, so the next page is already there when the reader
      // arrives rather than appearing under them.
      { rootMargin: '600px' },
    );

    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [onEndReached, hasMore]);

  return (
    <ol className="timeline" onScroll={props.onScrolled}>
      {props.items.map((item) => (
        <TimelineRow
          key={item.id}
          item={item}
          selected={item.id === props.selectedId}
          onSelect={props.onSelect}
          onToggleRead={props.onToggleRead}
          onToggleStar={props.onToggleStar}
        />
      ))}

      {props.hasMore && (
        <li className="timeline__sentinel" ref={sentinel}>
          {props.loading ? 'Loading…' : ''}
        </li>
      )}

      {!props.hasMore && props.items.length > 0 && (
        /*
         * The bottom of the list, said out loud. It is the product's central
         * claim (PRD §2) and the thing H9 measures; a list that simply stops
         * scrolling does not tell anybody they finished.
         */
        <li className="timeline__end">You have reached the end.</li>
      )}
    </ol>
  );
}

function TimelineRow({
  item,
  selected,
  onSelect,
  onToggleRead,
  onToggleStar,
}: {
  item: Item;
  selected: boolean;
  onSelect: (item: Item) => void;
  onToggleRead: (item: Item) => void;
  onToggleStar?: ((item: Item) => void) | undefined;
}): ReactNode {
  const row = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    if (!selected || row.current === null) {
      return;
    }
    /*
     * `nearest` rather than `center`: a reader moving down a list expects it
     * to scroll only when the selection would otherwise leave the viewport,
     * not to re-centre on every keystroke.
     */
    row.current.scrollIntoView({ block: 'nearest' });
    row.current.focus({ preventScroll: true });
  }, [selected]);

  return (
    <li
      ref={row}
      className={`timeline__row${selected ? ' is-selected' : ''}${item.read ? ' is-read' : ' is-unread'}`}
      tabIndex={selected ? 0 : -1}
      aria-current={selected ? 'true' : undefined}
      onClick={() => {
        onSelect(item);
      }}
    >
      <span className="timeline__dot" aria-hidden="true" />
      <span className="timeline__content">
        <span className="timeline__title">
          {item.title === '' ? 'Untitled' : item.title}
        </span>
        {item.snippet !== undefined && item.snippet !== null && (
          <span className="timeline__snippet">{item.snippet}</span>
        )}
        <span className="timeline__meta">
          {item.feedTitle ?? 'Unknown feed'} ·{' '}
          <time dateTime={item.publishedAt}>{relative(item.publishedAt)}</time>
          {item.tags.length > 0 && (
            <span className="timeline__tags"> · {item.tags.join(', ')}</span>
          )}
        </span>
      </span>
      {onToggleStar !== undefined && (
        <button
          className={`timeline__star${item.starred ? ' is-starred' : ''}`}
          aria-label={item.starred ? 'Unstar' : 'Star'}
          aria-pressed={item.starred}
          onClick={(event) => {
            event.stopPropagation();
            onToggleStar(item);
          }}
        >
          {item.starred ? '★' : '☆'}
        </button>
      )}
      <button
        className="timeline__mark"
        aria-label={item.read ? 'Mark unread' : 'Mark read'}
        onClick={(event) => {
          // Without this the row's own click handler also fires and the item
          // opens, which marks it read again.
          event.stopPropagation();
          onToggleRead(item);
        }}
      >
        {item.read ? '○' : '●'}
      </button>
    </li>
  );
}

/** Short, relative dates: a timeline is read by recency, not by calendar. */
function relative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) {
    return iso;
  }

  const minutes = Math.round((Date.now() - then) / 60_000);

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${String(minutes)}m ago`;
  if (minutes < 60 * 24) return `${String(Math.round(minutes / 60))}h ago`;
  if (minutes < 60 * 24 * 7) return `${String(Math.round(minutes / (60 * 24)))}d ago`;

  return new Date(then).toLocaleDateString(undefined, { dateStyle: 'medium' });
}
